<template>
    <div>
        <!-- Header -->
        <div class="compass-card compass-card--accent mb-4">
            <h1 class="text-gradient mb-2">Constellation (MCP) Fee Calculator</h1>
            <p class="mb-2">
                Anza's <a href="https://constellation.anza.xyz/" target="_blank" rel="noopener">Constellation</a> proposal
                introduces Multiple Concurrent Proposers (MCP) to Solana. Under this model, <strong>priority fees
                are pooled and redistributed to all validators by stake weight</strong> at the end of each epoch,
                replacing today's "lucky block" system where validators earn more when they catch high-activity slots.
            </p>
            <p class="mb-0 analytics-text-muted">
                This calculator uses historical EpochPerformance data to show how each validator's earnings would
                change under the proposed fee redistribution model.
            </p>
        </div>

        <!-- Validator Search -->
        <div class="compass-card mb-4">
            <div class="row align-items-center">
                <div class="col-md-6">
                    <label class="form-label fw-bold">Search for a Validator</label>
                    <el-autocomplete
                        v-model="searchQuery"
                        :fetch-suggestions="searchValidators"
                        @select="selectValidator"
                        placeholder="Type a validator name or address..."
                        :trigger-on-focus="false"
                        :debounce="300"
                        clearable
                        class="w-100"
                        popper-class="constellation-search-popper"
                    >
                        <template #default="{ item }">
                            <div class="d-flex justify-content-between align-items-center">
                                <div>
                                    <strong>{{ item.name }}</strong>
                                    <br>
                                    <small class="analytics-text-muted">{{ item.identity.substring(0, 16) }}...</small>
                                </div>
                                <div class="text-end">
                                    <span class="font-mono-num">{{ formatStake(item.active_stake) }}</span>
                                    <br>
                                    <small class="analytics-text-muted">{{ item.commission }}% comm</small>
                                </div>
                            </div>
                        </template>
                    </el-autocomplete>
                </div>
                <div class="col-md-6 mt-3 mt-md-0">
                    <label class="form-label fw-bold">Fee Model</label>
                    <div class="analytics-tabs">
                        <button class="tab-btn" :class="{ active: feeMode === 'total' }" @click="feeMode = 'total'">
                            Priority Fees + Jito
                        </button>
                        <button class="tab-btn" :class="{ active: feeMode === 'fees_only' }" @click="feeMode = 'fees_only'">
                            Priority Fees Only
                        </button>
                    </div>
                    <small class="analytics-text-muted d-block mt-1">
                        <template v-if="feeMode === 'total'">
                            Assumes Jito tips are also redistributed by stake weight under MCP.
                        </template>
                        <template v-else>
                            Only protocol priority fees are redistributed. Jito tips excluded.
                        </template>
                    </small>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mt-3 analytics-text-muted">Loading validator data...</p>
        </div>

        <!-- Error State -->
        <div v-if="error" class="compass-info-box compass-info-box--danger mb-4">
            {{ error }}
        </div>

        <!-- Results -->
        <div v-if="data && !loading">
            <!-- Validator Info Bar -->
            <div class="compass-pool-card mb-4">
                <div class="row align-items-center">
                    <div class="col-md-4">
                        <div class="pool-name">{{ data.validator.name }}</div>
                        <div class="pool-meta font-mono" style="font-size: 0.8em;">
                            {{ data.validator.identity.substring(0, 20) }}...
                        </div>
                    </div>
                    <div class="col-md-2 text-center">
                        <div class="pool-stat">
                            <div class="pool-stat-value font-mono-num">{{ formatStake(data.validator.active_stake) }}</div>
                            <div class="pool-stat-label">Active Stake</div>
                        </div>
                    </div>
                    <div class="col-md-2 text-center">
                        <div class="pool-stat">
                            <div class="pool-stat-value font-mono-num">{{ (data.summary.avg_stake_weight * 100).toFixed(4) }}%</div>
                            <div class="pool-stat-label">Avg Stake Weight</div>
                        </div>
                    </div>
                    <div class="col-md-2 text-center">
                        <div class="pool-stat">
                            <div class="pool-stat-value font-mono-num">{{ data.validator.commission }}%</div>
                            <div class="pool-stat-label">Commission</div>
                        </div>
                    </div>
                    <div class="col-md-2 text-center">
                        <div class="pool-stat">
                            <div class="pool-stat-value">
                                <span class="text-success">{{ data.summary.wins }}W</span> /
                                <span class="text-danger">{{ data.summary.losses }}L</span>
                            </div>
                            <div class="pool-stat-label">Epochs Won/Lost</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Summary Cards -->
            <div class="row mb-4">
                <div class="col-md-4 mb-3">
                    <div class="compass-stat-card">
                        <div class="stat-label">Current System Earnings</div>
                        <div class="stat-value font-mono-num">{{ formatSOL(currentTotal) }}</div>
                        <div class="stat-unit">SOL ({{ data.epochs.length }} epochs)</div>
                    </div>
                </div>
                <div class="col-md-4 mb-3">
                    <div class="compass-stat-card">
                        <div class="stat-label">Constellation Earnings</div>
                        <div class="stat-value font-mono-num">{{ formatSOL(constellationTotal) }}</div>
                        <div class="stat-unit">SOL (stake-weighted)</div>
                    </div>
                </div>
                <div class="col-md-4 mb-3">
                    <div class="compass-stat-card" :class="difference > 0 ? 'compass-stat-card--positive' : 'compass-stat-card--negative'">
                        <div class="stat-label">Net Difference</div>
                        <div class="stat-value font-mono-num">
                            {{ difference > 0 ? '+' : '' }}{{ formatSOL(difference) }}
                        </div>
                        <div class="stat-unit">
                            {{ differencePercent > 0 ? '+' : '' }}{{ differencePercent.toFixed(1) }}%
                            &mdash; validator {{ difference > 0 ? 'benefits' : 'loses' }} under Constellation
                        </div>
                    </div>
                </div>
            </div>

            <!-- Chart -->
            <div class="analytics-panel mb-4">
                <div class="panel-header">
                    <div>
                        <div class="panel-title">Epoch-by-Epoch Comparison</div>
                        <div class="panel-subtitle">
                            Current system earnings vs projected Constellation earnings
                        </div>
                    </div>
                </div>
                <div class="panel-body" style="padding: 1rem;">
                    <canvas ref="mainChart" height="350"></canvas>
                </div>
            </div>

            <!-- Cumulative Difference Chart -->
            <div class="analytics-panel mb-4">
                <div class="panel-header">
                    <div>
                        <div class="panel-title">Cumulative Gain / Loss</div>
                        <div class="panel-subtitle">
                            Running total of how much this validator would gain or lose under Constellation
                        </div>
                    </div>
                </div>
                <div class="panel-body" style="padding: 1rem;">
                    <canvas ref="cumulativeChart" height="250"></canvas>
                </div>
            </div>

            <!-- Breakdown Table -->
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Detailed Breakdown</div>
                </div>
                <div class="panel-body p-0">
                    <div class="table-mobile-scroll">
                        <el-table :data="data.epochs" stripe style="width: 100%"
                            :row-class-name="tableRowClass">
                            <el-table-column prop="epoch" label="Epoch" width="90" fixed />
                            <el-table-column label="Slots" width="90" align="center">
                                <template #default="{ row }">
                                    <span class="font-mono-num">{{ row.packed_slots }}</span>
                                    <span class="analytics-text-muted">/{{ row.num_slots }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column label="Stake Weight" width="110" align="right">
                                <template #default="{ row }">
                                    <span class="font-mono-num">{{ (row.stake_weight * 100).toFixed(4) }}%</span>
                                </template>
                            </el-table-column>
                            <el-table-column label="Current Fees" width="140" align="right">
                                <template #default="{ row }">
                                    <span class="font-mono-num">{{ formatSOL(currentEpochTotal(row)) }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column label="Constellation" width="140" align="right">
                                <template #default="{ row }">
                                    <span class="font-mono-num">{{ formatSOL(constellationEpochTotal(row)) }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column label="Difference" width="150" align="right">
                                <template #default="{ row }">
                                    <span class="font-mono-num" :class="epochDiff(row) >= 0 ? 'text-success' : 'text-danger'">
                                        {{ epochDiff(row) >= 0 ? '+' : '' }}{{ formatSOL(epochDiff(row)) }}
                                    </span>
                                </template>
                            </el-table-column>
                            <el-table-column label="Change %" width="100" align="right">
                                <template #default="{ row }">
                                    <span :class="epochDiffPct(row) >= 0 ? 'pct-change pct-positive' : 'pct-change pct-negative'">
                                        {{ epochDiffPct(row) >= 0 ? '+' : '' }}{{ epochDiffPct(row).toFixed(1) }}%
                                    </span>
                                </template>
                            </el-table-column>
                            <el-table-column label="Network Fees" width="140" align="right">
                                <template #default="{ row }">
                                    <span class="font-mono-num analytics-text-muted">
                                        {{ formatSOL(networkEpochTotal(row)) }}
                                    </span>
                                </template>
                            </el-table-column>
                            <el-table-column label="" width="50" align="center">
                                <template #default="{ row }">
                                    <span v-if="epochDiff(row) > 0" class="text-success">W</span>
                                    <span v-else-if="epochDiff(row) < 0" class="text-danger">L</span>
                                    <span v-else class="analytics-text-muted">=</span>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                </div>
            </div>

            <!-- Explanation -->
            <div class="compass-info-box mt-4">
                <h5>How this is calculated</h5>
                <p class="mb-2">
                    <strong>Current System:</strong> Validators earn priority fees (and Jito tips) from the blocks
                    they produce. A validator who happens to get leader slots during high-activity periods earns
                    disproportionately more &mdash; the "lucky block" effect.
                </p>
                <p class="mb-2">
                    <strong>Constellation (MCP):</strong> Priority fees are pooled across the entire epoch and
                    redistributed to all validators proportional to their stake weight. This smooths out variance
                    and rewards stake rather than slot luck.
                </p>
                <p class="mb-0">
                    <strong>Who benefits?</strong> Validators whose stake weight exceeds their share of
                    high-fee blocks benefit. Those who consistently "catch" high-value slots would earn less.
                    Small validators with consistent stake see the most predictable improvement.
                </p>
            </div>
        </div>

        <!-- Empty state -->
        <div v-if="!data && !loading && !error" class="text-center py-5">
            <div class="compass-info-box">
                <h4>Select a Validator</h4>
                <p>Search for a validator above to see how Constellation's fee redistribution would affect their earnings
                    compared to the current system over the last 15 epochs.</p>
            </div>
        </div>
    </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
import { colors, getThemeAwareColors } from '../composables/useChartConfig';
Chart.register(...registerables);

export default {
    name: 'ConstellationCalculator',
    data() {
        return {
            searchQuery: '',
            feeMode: 'total',
            loading: false,
            error: null,
            data: null,
            mainChart: null,
            cumulativeChart: null,
        };
    },
    computed: {
        currentTotal() {
            if (!this.data) return 0;
            return this.data.epochs.reduce((sum, e) => sum + this.currentEpochTotal(e), 0);
        },
        constellationTotal() {
            if (!this.data) return 0;
            return this.data.epochs.reduce((sum, e) => sum + this.constellationEpochTotal(e), 0);
        },
        difference() {
            return this.constellationTotal - this.currentTotal;
        },
        differencePercent() {
            return this.currentTotal > 0 ? (this.difference / this.currentTotal) * 100 : 0;
        },
    },
    watch: {
        feeMode() {
            if (this.data) {
                this.renderCharts();
            }
        },
    },
    beforeUnmount() {
        if (this.mainChart) this.mainChart.destroy();
        if (this.cumulativeChart) this.cumulativeChart.destroy();
    },
    methods: {
        currentEpochTotal(row) {
            if (this.feeMode === 'fees_only') return row.current_priority_fees;
            return row.current_priority_fees + row.current_jito;
        },
        constellationEpochTotal(row) {
            if (this.feeMode === 'fees_only') return row.constellation_priority_fees;
            return row.constellation_priority_fees + row.constellation_jito;
        },
        networkEpochTotal(row) {
            if (this.feeMode === 'fees_only') return row.network_priority_fees;
            return row.network_priority_fees + row.network_jito;
        },
        epochDiff(row) {
            return this.constellationEpochTotal(row) - this.currentEpochTotal(row);
        },
        epochDiffPct(row) {
            const current = this.currentEpochTotal(row);
            if (current === 0) return this.constellationEpochTotal(row) > 0 ? 100 : 0;
            return ((this.constellationEpochTotal(row) - current) / current) * 100;
        },
        formatSOL(lamports) {
            const sol = lamports / 1e9;
            if (Math.abs(sol) >= 1000) return sol.toLocaleString(undefined, { maximumFractionDigits: 1 }) + ' SOL';
            if (Math.abs(sol) >= 1) return sol.toLocaleString(undefined, { maximumFractionDigits: 3 }) + ' SOL';
            return sol.toLocaleString(undefined, { maximumFractionDigits: 6 }) + ' SOL';
        },
        formatStake(lamports) {
            const sol = lamports / 1e9;
            if (sol >= 1e6) return (sol / 1e6).toFixed(2) + 'M SOL';
            if (sol >= 1e3) return (sol / 1e3).toFixed(1) + 'K SOL';
            return sol.toFixed(0) + ' SOL';
        },
        searchValidators(queryString, cb) {
            if (!queryString || queryString.length < 2) {
                cb([]);
                return;
            }
            axios.get('/api/v1/constellation/search', { params: { q: queryString } })
                .then(res => cb(res.data))
                .catch(() => cb([]));
        },
        selectValidator(item) {
            this.searchQuery = item.name;
            this.fetchData(item.identity);
        },
        fetchData(identity) {
            this.loading = true;
            this.error = null;
            axios.get(`/api/v1/constellation/${identity}`)
                .then(res => {
                    this.data = res.data;
                    this.loading = false;
                    this.$nextTick(() => this.renderCharts());
                })
                .catch(err => {
                    this.error = err.response?.data?.error || 'Failed to load data';
                    this.data = null;
                    this.loading = false;
                });
        },
        tableRowClass({ row }) {
            const diff = this.epochDiff(row);
            if (diff > 0) return 'constellation-win-row';
            if (diff < 0) return 'constellation-loss-row';
            return '';
        },
        renderCharts() {
            this.renderMainChart();
            this.renderCumulativeChart();
        },
        renderMainChart() {
            if (this.mainChart) this.mainChart.destroy();
            const ctx = this.$refs.mainChart?.getContext('2d');
            if (!ctx) return;

            const theme = getThemeAwareColors();
            const epochs = this.data.epochs;
            const labels = epochs.map(e => 'E' + e.epoch);

            const currentData = epochs.map(e => this.currentEpochTotal(e) / 1e9);
            const constellationData = epochs.map(e => this.constellationEpochTotal(e) / 1e9);

            this.mainChart = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels,
                    datasets: [
                        {
                            label: 'Current System',
                            data: currentData,
                            backgroundColor: 'rgba(99, 102, 241, 0.7)',
                            borderColor: '#6366f1',
                            borderWidth: 1,
                            borderRadius: 3,
                        },
                        {
                            label: 'Constellation',
                            data: constellationData,
                            backgroundColor: 'rgba(139, 92, 246, 0.7)',
                            borderColor: '#8b5cf6',
                            borderWidth: 1,
                            borderRadius: 3,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: 'index',
                        intersect: false,
                    },
                    plugins: {
                        legend: {
                            position: 'top',
                            labels: { color: theme.text },
                        },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => {
                                    const val = ctx.raw;
                                    return `${ctx.dataset.label}: ${val.toFixed(4)} SOL`;
                                },
                                afterBody: (tooltipItems) => {
                                    const idx = tooltipItems[0].dataIndex;
                                    const diff = constellationData[idx] - currentData[idx];
                                    const pct = currentData[idx] > 0
                                        ? ((diff / currentData[idx]) * 100).toFixed(1)
                                        : '0';
                                    return [
                                        '',
                                        `Difference: ${diff >= 0 ? '+' : ''}${diff.toFixed(4)} SOL (${diff >= 0 ? '+' : ''}${pct}%)`,
                                    ];
                                },
                            },
                        },
                    },
                    scales: {
                        x: {
                            ticks: { color: theme.textMuted },
                            grid: { color: theme.grid },
                        },
                        y: {
                            title: {
                                display: true,
                                text: 'SOL',
                                color: theme.textMuted,
                            },
                            ticks: {
                                color: theme.textMuted,
                                callback: (v) => v.toFixed(2),
                            },
                            grid: { color: theme.grid },
                        },
                    },
                },
            });
        },
        renderCumulativeChart() {
            if (this.cumulativeChart) this.cumulativeChart.destroy();
            const ctx = this.$refs.cumulativeChart?.getContext('2d');
            if (!ctx) return;

            const theme = getThemeAwareColors();
            const epochs = this.data.epochs;
            const labels = epochs.map(e => 'E' + e.epoch);

            // Build cumulative difference
            let cumulative = 0;
            const cumulativeData = epochs.map(e => {
                cumulative += (this.constellationEpochTotal(e) - this.currentEpochTotal(e));
                return cumulative / 1e9;
            });

            // Per-epoch difference bars
            const diffBars = epochs.map(e => (this.constellationEpochTotal(e) - this.currentEpochTotal(e)) / 1e9);
            const barColors = diffBars.map(d => d >= 0 ? 'rgba(34, 197, 94, 0.6)' : 'rgba(239, 68, 68, 0.6)');

            this.cumulativeChart = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels,
                    datasets: [
                        {
                            label: 'Per-Epoch Difference',
                            data: diffBars,
                            backgroundColor: barColors,
                            borderWidth: 0,
                            borderRadius: 2,
                            order: 2,
                        },
                        {
                            label: 'Cumulative Gain/Loss',
                            data: cumulativeData,
                            type: 'line',
                            borderColor: cumulative >= 0 ? colors.positive : colors.negative,
                            backgroundColor: cumulative >= 0 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                            borderWidth: 2.5,
                            pointRadius: 3,
                            pointBackgroundColor: cumulative >= 0 ? colors.positive : colors.negative,
                            fill: true,
                            tension: 0.3,
                            order: 1,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: 'index',
                        intersect: false,
                    },
                    plugins: {
                        legend: {
                            position: 'top',
                            labels: { color: theme.text },
                        },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => {
                                    const val = ctx.raw;
                                    return `${ctx.dataset.label}: ${val >= 0 ? '+' : ''}${val.toFixed(4)} SOL`;
                                },
                            },
                        },
                    },
                    scales: {
                        x: {
                            ticks: { color: theme.textMuted },
                            grid: { color: theme.grid },
                        },
                        y: {
                            title: {
                                display: true,
                                text: 'SOL',
                                color: theme.textMuted,
                            },
                            ticks: {
                                color: theme.textMuted,
                                callback: (v) => (v >= 0 ? '+' : '') + v.toFixed(3),
                            },
                            grid: { color: theme.grid },
                        },
                    },
                },
            });
        },
    },
};
</script>

<style scoped>
:deep(.constellation-win-row) td {
    background-color: rgba(34, 197, 94, 0.04) !important;
}
:deep(.constellation-loss-row) td {
    background-color: rgba(239, 68, 68, 0.04) !important;
}
</style>
